(() => {
  const FORM_ROOT_ID = 'forms-root-1092599';
  const DATE_GROUP_SELECTOR = '[data-testid="date-birthday"]';
  const PICKER_SELECTOR = '[data-vermore-appointment-date-picker]';
  const observedRoots = new WeakSet();
  let scanQueued = false;

  function collectOpenRoots(scope, roots = []) {
    roots.push(scope);
    scope.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) collectOpenRoots(element.shadowRoot, roots);
    });
    return roots;
  }

  function labelledTextFor(input) {
    const labelledBy = input.getAttribute('aria-labelledby') || '';
    const root = input.getRootNode?.() || input.ownerDocument;

    return labelledBy
      .split(/\s+/)
      .filter(Boolean)
      .map((labelId) => root?.getElementById?.(labelId)?.textContent || '')
      .join(' ');
  }

  function inputSignature(input) {
    return [
      input.getAttribute('placeholder'),
      input.getAttribute('aria-label'),
      input.getAttribute('aria-labelledby'),
      input.getAttribute('data-testid'),
      labelledTextFor(input),
      input.name,
      input.id,
    ]
      .filter(Boolean)
      .join(' ')
      .toUpperCase();
  }

  function datePartFor(input) {
    const signature = inputSignature(input);

    if (/(^|[^A-Z])YYYY([^A-Z]|$)|YEAR/.test(signature)) return 'year';
    if (/(^|[^A-Z])MM([^A-Z]|$)|MONTH/.test(signature)) return 'month';
    if (/(^|[^A-Z])DD([^A-Z]|$)|DAY/.test(signature)) return 'day';
    return null;
  }

  function segmentedInputs(group) {
    const parts = {};

    collectOpenRoots(group).forEach((root) => {
      root.querySelectorAll('input').forEach((input) => {
        const part = datePartFor(input);
        if (part && !parts[part]) parts[part] = input;
      });
    });

    return parts.year && parts.month && parts.day ? parts : null;
  }

  function setControlledValue(input, value) {
    const InputElement = input.ownerDocument.defaultView.HTMLInputElement;
    const valueSetter = Object.getOwnPropertyDescriptor(InputElement.prototype, 'value')?.set;

    if (valueSetter) {
      valueSetter.call(input, value);
    } else {
      input.value = value;
    }

    const InputEventConstructor = input.ownerDocument.defaultView.InputEvent;
    const inputEvent = InputEventConstructor
      ? new InputEventConstructor('input', {
          bubbles: true,
          composed: true,
          inputType: 'insertText',
          data: value,
        })
      : new Event('input', { bubbles: true, composed: true });

    input.dispatchEvent(inputEvent);
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  function pickerValueFromSegments(parts) {
    const year = String(parts.year.value || '').trim();
    const month = String(parts.month.value || '').trim().padStart(2, '0');
    const day = String(parts.day.value || '').trim().padStart(2, '0');

    if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(month) || !/^\d{2}$/.test(day)) return '';

    const candidate = `${year}-${month}-${day}`;
    const parsed = new Date(`${candidate}T00:00:00`);
    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getFullYear() !== Number(year) ||
      parsed.getMonth() + 1 !== Number(month) ||
      parsed.getDate() !== Number(day)
    ) {
      return '';
    }

    return candidate;
  }

  function syncSegmentsFromPicker(picker, parts) {
    const match = picker.value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const values = match
      ? { year: match[1], month: match[2], day: match[3] }
      : { year: '', month: '', day: '' };

    setControlledValue(parts.year, values.year);
    setControlledValue(parts.month, values.month);
    setControlledValue(parts.day, values.day);
  }

  function pickerLabel() {
    return document.documentElement.lang.toLowerCase().startsWith('en') ? 'Select a date' : '請選擇日期';
  }

  function buildPicker(group, parts) {
    const wrapper = document.createElement('div');
    const label = document.createElement('label');
    const picker = document.createElement('input');
    const pickerId = `vermore-appointment-date-${Math.random().toString(36).slice(2)}`;

    wrapper.setAttribute('data-vermore-appointment-date-picker', '');
    wrapper.vermoreDateParts = parts;
    wrapper.style.cssText = 'display:flex;flex-direction:column;gap:8px;width:100%;text-align:left;';

    label.htmlFor = pickerId;
    label.textContent = pickerLabel();
    label.style.cssText = 'font:inherit;color:inherit;';

    picker.id = pickerId;
    picker.type = 'date';
    picker.autocomplete = 'off';
    picker.required = Object.values(parts).some(
      (input) => input.required || input.getAttribute('aria-required') === 'true'
    );
    picker.setAttribute('aria-label', pickerLabel());
    picker.style.cssText =
      'box-sizing:border-box;width:100%;min-height:48px;padding:10px 12px;border:1px solid #8a8a8a;border-radius:2px;background:#fff;color:#202020;font:inherit;color-scheme:light;';
    picker.value = pickerValueFromSegments(parts);

    picker.addEventListener('input', () => syncSegmentsFromPicker(picker, parts));
    Object.values(parts).forEach((input) => {
      const syncPicker = () => {
        picker.value = pickerValueFromSegments(parts);
      };
      input.addEventListener('input', syncPicker);
      input.addEventListener('change', syncPicker);
    });

    wrapper.append(label, picker);
    group.before(wrapper);
    group.hidden = true;
    group.setAttribute('aria-hidden', 'true');
    group.setAttribute('data-vermore-original-date-segments', '');
    return wrapper;
  }

  function enhanceDateGroup(group) {
    const parts = segmentedInputs(group);
    if (!parts) return false;

    const previous = group.previousElementSibling;
    if (
      previous?.matches(PICKER_SELECTOR) &&
      previous.vermoreDateParts?.year === parts.year &&
      previous.vermoreDateParts?.month === parts.month &&
      previous.vermoreDateParts?.day === parts.day
    ) {
      group.hidden = true;
      group.setAttribute('aria-hidden', 'true');
      return true;
    }

    if (previous?.matches(PICKER_SELECTOR)) previous.remove();
    buildPicker(group, parts);
    return true;
  }

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(scan);
  }

  function observeRoot(root) {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(queueScan).observe(root, { childList: true, subtree: true });
  }

  function scan() {
    scanQueued = false;
    const formRoot =
      document.getElementById(FORM_ROOT_ID) || document.querySelector(`[data-forms-id="${FORM_ROOT_ID}"]`);
    if (!formRoot) return false;

    collectOpenRoots(formRoot).forEach((root) => {
      observeRoot(root);
      root.querySelectorAll(DATE_GROUP_SELECTOR).forEach(enhanceDateGroup);
    });
    return true;
  }

  function init() {
    observeRoot(document.body);
    scan();
  }

  window.VermoreAppointmentDatePicker = {
    datePartFor,
    enhanceDateGroup,
    pickerValueFromSegments,
    scan,
    segmentedInputs,
    syncSegmentsFromPicker,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
